/* ============================================
   El perfil de la cuenta, compartido

   Era un outlet context del layout de Mi perfil, así que solo lo veían sus
   rutas hijas. Al separarse Mi perfil (la persona) de Configuración (la
   empresa y el equipo) pasaron a ser dos árboles de rutas distintos que
   necesitan lo mismo, y un contexto de React sirve a los dos sin que ninguno
   tenga que pasárselo al otro.
   ============================================ */
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { showToast } from '@/components/layout/Toast'

export interface Perfil {
  email: string
  /** `foto_url` sale de `usuarios`: es la foto de quien USA el ERP, la misma
   *  que lee el pie del sidebar. */
  personal: { nombre: string; telefono: string; foto_url?: string | null }
  empresa: {
    id: number
    nombre_empresa: string
    /** El logo de la empresa para su panel. La identidad de un documento de
     *  marca blanca es otra cosa y vive en `marcas`. */
    logo_url?: string | null
    facturacion: { tipo_documento_fiscal: string; ruc_dni: string; razon_social: string; direccion_fiscal: string }
    puede_editar_facturacion: boolean
    mi_rol: string
  }
}

interface PerfilCtx {
  perfil: Perfil | null
  loading: boolean
  reload: () => void
}

const Contexto = createContext<PerfilCtx>({ perfil: null, loading: true, reload: () => {} })

/** El perfil compartido. Dentro de `PerfilProvider`. */
export function usePerfilCtx(): PerfilCtx {
  return useContext(Contexto)
}

export function PerfilProvider({ children }: { children: React.ReactNode }) {
  const api = useApi()
  const { activeClienteId } = usePortalCliente()
  const [perfil, setPerfil] = useState<Perfil | null>(null)
  const [loading, setLoading] = useState(true)

  const reload = useCallback(() => {
    setLoading(true)
    api.get<Perfil>('/api/portal/perfil')
      .then(p => setPerfil(p))
      .catch(() => showToast('No se pudo cargar tu perfil', 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Carga al montar y al cambiar de empresa activa (el JWT ya se re-scopeó).
  useEffect(() => { reload() }, [activeClienteId, reload])

  return <Contexto.Provider value={{ perfil, loading, reload }}>{children}</Contexto.Provider>
}
